import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import Swal from "sweetalert2";
import axios from "axios";
import "./AddTenantContact.css";
import { API_BASE } from "../../../ApiUrl";

const API_URL = `${API_BASE}/tenant-authorised-representatives/`;

const emptyRepresentative = {
  full_name: "",
  title: "",
  official_email: "",
  mobile: "",
  authority_type: "",
  effective_from: "",
  effective_to: ""
};

const AddAuthorizedRepresentative = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const editRepresentative =
    location.state?.editRepresentative || null;

  const isEditMode = Boolean(editRepresentative?.id);

  const [representatives, setRepresentatives] = useState(() => {
    if (editRepresentative) {
      return [
        {
          full_name: editRepresentative.full_name || "",
          title: editRepresentative.title || "",
          official_email: editRepresentative.official_email || "",
          mobile: editRepresentative.mobile || "",
          authority_type: editRepresentative.authority_type || "",
          effective_from: editRepresentative.effective_from
            ? editRepresentative.effective_from.substring(0, 10)
            : "",
          effective_to: editRepresentative.effective_to
            ? editRepresentative.effective_to.substring(0, 10)
            : ""
        }
      ];
    }

    return [{ ...emptyRepresentative }];
  });

  const [loading, setLoading] = useState(false);

  const getTenantUser = () => {
    try {
      const tenantUser = localStorage.getItem("tenant_user");

      return tenantUser ? JSON.parse(tenantUser) : null;
    } catch (error) {
      console.error("Error reading tenant_user:", error);
      return null;
    }
  };

  const handleChange = (index, e) => {
    const { name, value } = e.target;

    setRepresentatives((prev) =>
      prev.map((representative, i) =>
        i === index
          ? {
              ...representative,
              [name]: value
            }
          : representative
      )
    );
  };

  const addRepresentative = () => {
    if (isEditMode) return;

    setRepresentatives((prev) => [
      ...prev,
      { ...emptyRepresentative }
    ]);
  };

  const removeRepresentative = (index) => {
    if (isEditMode || representatives.length === 1) {
      return;
    }

    setRepresentatives((prev) =>
      prev.filter((_, i) => i !== index)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const tenantUser = getTenantUser();

    if (!tenantUser) {
      Swal.fire({
        icon: "error",
        title: "Session Information Missing",
        text: "Tenant user information was not found in local storage."
      });

      return;
    }

    if (!tenantUser.tenant_id || !tenantUser.user_id) {
      Swal.fire({
        icon: "error",
        title: "Invalid Tenant User",
        text: "Tenant ID or User ID is missing from tenant_user."
      });

      return;
    }

    for (let i = 0; i < representatives.length; i++) {
      const representative = representatives[i];

      if (
        !representative.full_name.trim() ||
        !representative.official_email.trim() ||
        !representative.mobile.trim() ||
        !representative.authority_type
      ) {
        Swal.fire({
          icon: "warning",
          title: "Required Fields Missing",
          text: `Please complete Full Name, Official Email, Mobile and Authority Type for Representative ${
            i + 1
          }.`
        });

        return;
      }
    }

    try {
      setLoading(true);

      if (isEditMode) {
        const representative = representatives[0];

        const payload = {
          full_name: representative.full_name.trim(),
          title: representative.title.trim(),
          official_email: representative.official_email.trim(),
          mobile: representative.mobile.trim(),
          authority_type: representative.authority_type,
          effective_from:
            representative.effective_from || null,
          effective_to:
            representative.effective_to || null,
          tenant: tenantUser.tenant_id,
          user: tenantUser.user_id
        };

        console.log(
          "Update Authorized Representative Payload:",
          payload
        );

        const response = await axios.put(
          `${API_URL}${editRepresentative.id}/`,
          payload,
          {
            headers: {
              "Content-Type": "application/json"
            }
          }
        );

        console.log(
          "Update Authorized Representative Response:",
          response.data
        );

        await Swal.fire({
          icon: "success",
          title: "Representative Updated Successfully",
          text: "Authorised representative has been updated successfully.",
          confirmButtonText: "OK"
        });

        navigate("/admin/auth-rep");
        return;
      }

      const payload = representatives.map((representative) => ({
        full_name: representative.full_name.trim(),
        title: representative.title.trim(),
        official_email: representative.official_email.trim(),
        mobile: representative.mobile.trim(),
        authority_type: representative.authority_type,
        effective_from:
          representative.effective_from || null,
        effective_to:
          representative.effective_to || null,
        tenant: tenantUser.tenant_id,
        user: tenantUser.user_id
      }));

      console.log(
        "Create Authorized Representatives Payload:",
        payload
      );

      const response = await axios.post(API_URL, payload, {
        headers: {
          "Content-Type": "application/json"
        }
      });

      console.log(
        "Create Authorized Representatives Response:",
        response.data
      );

      await Swal.fire({
        icon: "success",
        title: "Representatives Created Successfully",
        text: `${representatives.length} representative${
          representatives.length > 1 ? "s" : ""
        } created successfully.`,
        confirmButtonText: "OK"
      });

      navigate("/admin/auth-rep");
    } catch (error) {
      console.error(
        isEditMode
          ? "Update Authorized Representative Error:"
          : "Create Authorized Representatives Error:",
        error
      );

      let errorMessage = isEditMode
        ? "Unable to update authorised representative."
        : "Unable to create authorised representatives.";

      if (error.response?.data) {
        if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.errors) {
          errorMessage = Object.entries(
            error.response.data.errors
          )
            .map(
              ([field, errors]) =>
                `${field}: ${
                  Array.isArray(errors)
                    ? errors.join(", ")
                    : errors
                }`
            )
            .join(" | ");
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
      }

      Swal.fire({
        icon: "error",
        title: isEditMode ? "Update Failed" : "Save Failed",
        text: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin/auth-rep");
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="tenant-contact-page">
          <div className="tenant-contact-page-header">
            <div>
              <h2>
                {isEditMode
                  ? "Edit Authorised Representative"
                  : "Create Authorised Representative"}
              </h2>

              <p>
                {isEditMode
                  ? "Update authorised representative information."
                  : "Add one or multiple authorised representatives."}
              </p>
            </div>

            <button
              type="button"
              className="tenant-contact-back-btn"
              onClick={handleCancel}
            >
              ← Back
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="tenant-contact-card">
              <div className="tenant-contact-form-body">
                {representatives.map(
                  (representative, index) => (
                    <div
                      className="tenant-contact-record"
                      key={index}
                    >
                      <div className="tenant-contact-record-header">
                        <div>
                          <h4>
                            {isEditMode
                              ? "Authorised Representative Details"
                              : `Representative ${index + 1}`}
                          </h4>
                        </div>

                        {!isEditMode &&
                          representatives.length > 1 && (
                            <button
                              type="button"
                              className="tenant-contact-remove-btn"
                              onClick={() =>
                                removeRepresentative(index)
                              }
                            >
                              Remove
                            </button>
                          )}
                      </div>

                      <div className="tenant-contact-form-grid">
                        <div className="tenant-contact-form-group">
                          <label>
                            Full Name <span>*</span>
                          </label>

                          <input
                            type="text"
                            name="full_name"
                            value={representative.full_name}
                            onChange={(e) =>
                              handleChange(index, e)
                            }
                            placeholder="Enter full name"
                            maxLength={255}
                            required
                          />
                        </div>

                        <div className="tenant-contact-form-group">
                          <label>Title</label>

                          <input
                            type="text"
                            name="title"
                            value={representative.title}
                            onChange={(e) =>
                              handleChange(index, e)
                            }
                            placeholder="Enter title"
                            maxLength={255}
                          />
                        </div>

                        <div className="tenant-contact-form-group">
                          <label>
                            Official Email <span>*</span>
                          </label>

                          <input
                            type="email"
                            name="official_email"
                            value={
                              representative.official_email
                            }
                            onChange={(e) =>
                              handleChange(index, e)
                            }
                            placeholder="Enter official email"
                            required
                          />
                        </div>

                        <div className="tenant-contact-form-group">
                          <label>
                            Mobile <span>*</span>
                          </label>

                          <input
                            type="tel"
                            name="mobile"
                            value={representative.mobile}
                            onChange={(e) =>
                              handleChange(index, e)
                            }
                            placeholder="+919876543210"
                            maxLength={20}
                            required
                          />

                          <small>
                            Enter with country code (e.g.,
                            +919876543210)
                          </small>
                        </div>

                        <div className="tenant-contact-form-group">
                          <label>
                            Authority Type <span>*</span>
                          </label>

                          <select
                            name="authority_type"
                            value={
                              representative.authority_type
                            }
                            onChange={(e) =>
                              handleChange(index, e)
                            }
                            required
                          >
                            <option value="">
                              Select authority type
                            </option>

                            <option value="DIRECTOR">
                              Director
                            </option>

                            <option value="POWER_OF_ATTORNEY">
                              Power of Attorney
                            </option>

                            <option value="DESIGNATED_SIGNATORY">
                              Designated Signatory
                            </option>
                          </select>
                        </div>

                        <div className="tenant-contact-form-group">
                          <label>Effective From</label>

                          <input
                            type="date"
                            name="effective_from"
                            value={
                              representative.effective_from
                            }
                            onChange={(e) =>
                              handleChange(index, e)
                            }
                          />
                        </div>

                        <div className="tenant-contact-form-group">
                          <label>Effective To</label>

                          <input
                            type="date"
                            name="effective_to"
                            value={
                              representative.effective_to
                            }
                            onChange={(e) =>
                              handleChange(index, e)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  )
                )}

                {!isEditMode && (
                  <button
                    type="button"
                    className="tenant-contact-add-btn"
                    onClick={addRepresentative}
                  >
                    <span>+</span>
                    Add Another Representative
                  </button>
                )}
              </div>
            </div>

            <div className="tenant-contact-form-actions">
              <button
                type="button"
                className="tenant-contact-cancel-btn"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="tenant-contact-save-btn"
                disabled={loading}
              >
                {loading
                  ? isEditMode
                    ? "Updating..."
                    : "Saving..."
                  : isEditMode
                  ? "Update Representative"
                  : `Save ${representatives.length} Representative${
                      representatives.length > 1 ? "s" : ""
                    }`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddAuthorizedRepresentative;